import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../Api/ApiUrls";
import ViewModal from "./ViewModal";
import { styled } from "@mui/material/styles";

const SmallImage = styled("img")({
  width: "50px",
  height: "50px",
  objectFit: "cover",
  cursor: "pointer",
  borderRadius: "4px",
  "&:hover": {
    opacity: 0.8,
  },
});

const ImageModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ModalImage = styled("img")({
  maxWidth: "90%",
  maxHeight: "90%",
});

const BestPlanTable = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [changeMobileDialogOpen, setChangeMobileDialogOpen] = useState(false);
  const [newMobile, setNewMobile] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);

  const getImageUrl = (imagePath) => `${BASE_URL}${imagePath}`;

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/property/`);
      const bestdealProperties = response.data.filter(
        (property) => property.type === "best-deal"
      );
      setProperties(bestdealProperties);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to fetch properties. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleDelete = async (property_id) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        await axios.delete(`${BASE_URL}/property/${property_id}/`);
        toast.success("Property deleted successfully");
        fetchProperties();
      } catch (error) {
        console.error("Error deleting property:", error);
        toast.error("Failed to delete property. Please try again.");
      }
    }
  };

  const handleSearchChange = (event) => setSearch(event.target.value);

  const handleViewClick = (property) => {
    setCurrentProperty(property);
    setViewModalOpen(true);
  };

  const handleStatusChange = async (propertyId, newStatus) => {
    const previousStatus = properties.find(
      (prop) => prop.property_id === propertyId
    )?.status;
    const adminMobile = properties.find(
      (prop) => prop.property_id === propertyId
    )?.admin_mobile;

    const payload = [
      {
        property_id: propertyId,
        admin_mobile: adminMobile,
        Admin_status: newStatus,
      },
    ];

    try {
      await axios.put(`${BASE_URL}/properties-update/`, payload);

      setProperties((prev) =>
        prev.map((prop) =>
          prop.property_id === propertyId ? { ...prop, status: newStatus } : prop
        )
      );

      toast.success("Status updated successfully");
      fetchProperties();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");

      setProperties((prev) =>
        prev.map((prop) =>
          prop.property_id === propertyId
            ? { ...prop, status: previousStatus }
            : prop
        )
      );
    }
  };

  const handleSelectProperty = (propertyId) => {
    setSelectedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleOpenChangeMobileDialog = () => {
    setChangeMobileDialogOpen(true);
    setNewMobile("");
  };

  const handleCloseChangeMobileDialog = () => setChangeMobileDialogOpen(false);

  const handleMobileChangeSubmit = async () => {
    if (!newMobile) {
      toast.error("Please enter a mobile number");
      return;
    }

    const payload = selectedProperties.map((propertyId) => {
      const property = properties.find((p) => p.property_id === propertyId);
      return {
        property_id: propertyId,
        admin_mobile: newMobile,
        Admin_status: property?.Admin_status || "Pending",
      };
    });

    try {
      await axios.put(`${BASE_URL}/properties-update/`, payload);
      toast.success("Mobile numbers updated successfully");
      fetchProperties();
      setSelectedProperties([]);
      setChangeMobileDialogOpen(false);
    } catch (error) {
      console.error("Error updating mobile numbers:", error);
      toast.error("Failed to update mobile numbers");
    }
  };

  const handleImageClick = (imageUrl, imageId, property_id) => {
    setSelectedImage(imageUrl);
    setSelectedImageId(imageId);
    setSelectedPropertyId(property_id);
    setImageModalOpen(true);
  };

  const handleDeleteImage = async () => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const formData = new FormData();
      formData.append("deleted_image_ids", selectedImageId.toString());

      const response = await fetch(`${BASE_URL}/property/${selectedPropertyId}/`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) throw new Error(`Server responded with status ${response.status}`);

      await response.json();
      fetchProperties();
      setImageModalOpen(false);
      alert("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
      alert(`Deletion failed: ${error.message}`);
    }
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage("");
  };

  const filteredProperties = properties.filter((property) => {
    const searchTerm = search.toLowerCase();
    const priceString = property.price?.toString() || "";
    const areaString = property.site_area?.toString() || "";

    return (
      property.property_name?.toLowerCase().includes(searchTerm) ||
      property.posted_by?.toLowerCase().includes(searchTerm) ||
      priceString.includes(searchTerm) ||
      areaString.includes(searchTerm)
    );
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 2 }}>
      <Box
        sx={{
          mx: "auto",
          p: 1,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
          width: "128%",
          marginLeft: "-14%",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Best Deal Properties
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            label="Search Properties"
            variant="outlined"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by name or location..."
            size="small"
            sx={{ width: 300 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenChangeMobileDialog}
            disabled={selectedProperties.length === 0}
          >
            Change Mobile
          </Button>
        </Box>

        {/* Scrollable Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflowY: "auto" }}>
          <Table stickyHeader>
          <TableHead>
  <TableRow>
    <TableCell
      sx={{
        backgroundColor: "#1976d2",
        color: "white",
        textAlign: "center",
        fontSize: 16,
        fontWeight: "bold",
        position: "sticky",
        top: 0,
        zIndex: 1
      }}
    >
      <strong>Select</strong>
    </TableCell>

    {[
      "S.No",
      "Name",
      "Images",
      "User Mobile",
      "Admin Mobile",
      "Posted By",
      "Facing",
      "Site Area",
      "Price",
      "Location",
      "Status",
      "Actions",
    ].map((head) => (
      <TableCell
        key={head}
        sx={{
          backgroundColor: "#1976d2",
          color: "white",
          textAlign: "center",
          fontSize: 16,
        
          position: "sticky",
          top: 0,
          zIndex: 1
        }}
      >
        <strong>{head}</strong>
      </TableCell>
    ))}
  </TableRow>
</TableHead>

            <TableBody>
              {filteredProperties.map((property, index) => (
                <TableRow key={property.property_id}>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Checkbox
                      checked={selectedProperties.includes(property.property_id)}
                      onChange={() => handleSelectProperty(property.property_id)}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{index + 1}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.property_name}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {property.property_images?.slice(0, 4).map((img) => (
                      <SmallImage
                        key={img.id}
                        src={getImageUrl(img.image)}
                        onClick={() =>
                          handleImageClick(getImageUrl(img.image), img.id, property.property_id)
                        }
                        onError={(e) => (e.target.src = "https://via.placeholder.com/50")}
                      />
                    ))}
                    {property.property_images?.length > 4 && (
                      <Box
                        sx={{
                          position: "relative",
                          display: "inline-block",
                          width: "50px",
                          height: "50px",
                        }}
                      >
                        <SmallImage
                          src={getImageUrl(property.property_images[0].image)}
                          onClick={() =>
                            handleImageClick(
                              getImageUrl(property.property_images[0].image),
                              property.property_images[0].id,
                              property.property_id
                            )
                          }
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                          }}
                        >
                          +{property.property_images.length - 4}
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.mobile_no}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.admin_mobile}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.posted_by}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.facing}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.site_area}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.price}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{property.location}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Select
                      value={property.Admin_status || "Pending"}
                      onChange={(e) => handleStatusChange(property.property_id, e.target.value)}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton color="info" onClick={() => handleViewClick(property)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(property.property_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* View Modal */}
      <ViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        property={currentProperty}
      />

      {/* Change Mobile Dialog */}
      <Dialog open={changeMobileDialogOpen} onClose={handleCloseChangeMobileDialog}>
        <DialogTitle>Change Mobile Number</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Mobile Number"
            type="text"
            fullWidth
            variant="outlined"
            value={newMobile}
            onChange={(e) => setNewMobile(e.target.value)}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This will update the admin_mobile field for {selectedProperties.length} selected properties.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeMobileDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleMobileChangeSubmit} color="primary" variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <ImageModal open={imageModalOpen} onClose={handleCloseImageModal}>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <ModalImage
            src={selectedImage}
            alt="Enlarged property image"
            onError={(e) => (e.target.src = "https://via.placeholder.com/500")}
          />
          <IconButton
            onClick={handleDeleteImage}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(255,255,255,0.8)",
              '&:hover': { backgroundColor: "rgba(255,255,255,1)", transform: "scale(1.1)" },
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              '& .MuiSvgIcon-root': { fontSize: "1.5rem" },
            }}
          >
            <DeleteIcon color="error" />
          </IconButton>
        </Box>
      </ImageModal>
    </Container>
  );
};

export default BestPlanTable;
